import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { supabase } from "@/integrations/db/client";
import type { TablesUpdate } from "@/integrations/db/types.generated";
import { nextPaletteColor } from "@/lib/teamColors";

/**
 * A team: the organising unit for people since #3492.
 *
 * Teams group PEOPLE. Subsheets ("company sets") group TICKETS. The two are
 * independent — a team works tickets in any set — so nothing here touches
 * subsheets.
 */
export interface Team {
  id: string;
  name: string;
  description: string | null;
  /** Chip colour, #rrggbb. Read it through teamColor(), which handles the gaps. */
  color: string;
  order_index: number;
  created_by_id: string;
  created_at: string;
}

export const useTeams = () =>
  useQuery<Team[]>({
    queryKey: ["teams"],
    queryFn: async () => {
      const { data, error } = await supabase.from("teams").select("*").order("order_index", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    // Realtime keeps this honest (useGlobalTeamsSync), so it never needs a
    // timed refetch — the same reasoning as useSubsheets.
    staleTime: Infinity,
    gcTime: 24 * 60 * 60 * 1000,
  });

/**
 * Creates a team. Superadmin-only, enforced by the teams_appointment trigger —
 * the mutation does not pre-check, so the failure the user sees is the real one
 * rather than a guess the client made.
 */
export const useCreateTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, description }: { name: string; description?: string }) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Append, and colour by position, mirroring useCreateSubsheet's
      // max(order_index) read.
      const { data: existing } = await supabase.from("teams").select("order_index").order("order_index", {
        ascending: false,
      });
      const count = existing?.length ?? 0;
      const nextIndex = (existing?.[0]?.order_index ?? -1) + 1;

      const { data, error } = await supabase
        .from("teams")
        .insert({
          name: name.trim(),
          description: description?.trim() || null,
          color: nextPaletteColor(count),
          order_index: nextIndex,
          created_by_id: user.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data as Team;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      toast.success("Team created.");
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      toast.error(
        // The trigger and the unique constraint are the two realistic failures,
        // and each has a sentence worth more than "could not create team".
        /only a superadmin/i.test(message)
          ? "Only a superadmin can create a team."
          : /duplicate|unique/i.test(message)
            ? "A team with that name already exists."
            : message,
      );
    },
  });
};

export const useUpdateTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      name,
      description,
      color,
    }: {
      id: string;
      name?: string;
      description?: string | null;
      color?: string;
    }) => {
      // Typed against the generated row: a column that does not exist is a
      // typecheck error here rather than a 400 at runtime.
      const patch: TablesUpdate<"teams"> = {};
      if (name !== undefined) patch.name = name.trim();
      if (description !== undefined) patch.description = description?.trim() || null;
      if (color !== undefined) patch.color = color;

      const { error } = await supabase.from("teams").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      toast.success("Team updated.");
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      toast.error(/only a superadmin/i.test(message) ? "Only a superadmin can change a team." : message);
    },
  });
};

/**
 * Deletes a team.
 *
 * The database cleans up after itself: memberships cascade, and the
 * teams_scrub_references trigger strips the team from every ticket that tagged
 * it or sat in its queue. So tickets survive; they simply stop being that
 * team's. Invalidate backlog data too, because those rows just changed.
 */
export const useDeleteTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("teams").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      queryClient.invalidateQueries({ queryKey: ["team-memberships"] });
      queryClient.invalidateQueries({ queryKey: ["backlog-items"] });
      queryClient.invalidateQueries({ queryKey: ["backlog-item"] });
      toast.success("Team deleted. Its tickets kept, untagged.");
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      toast.error(/only a superadmin/i.test(message) ? "Only a superadmin can delete a team." : message);
    },
  });
};
